import { decSum, formatDec } from '@felix/domain';
import { Alert, Button, Modal, SegmentedControl, Select, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconChartBar,
  IconCoins,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconGasStation,
  IconGauge,
  IconMapPin,
  IconSearch,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { followLink, useShell } from '../../shell-context.js';
import { renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { downloadFile, reportWorkbook, XLSX_TYPE } from '../reports/xlsx.js';
import classes from './Ledger.module.css';
import { mt, useLedgerError } from './ledger-ui.js';
import cx from './Reconciliation.module.css';
import { analysisFuels, analysisWorkbookTable, tablePdf } from './reconciliation-form.js';
import type { FuelSource } from './source.js';
import type { FuelAnalysisData, FuelAnalysisFilters, FuelAnalysisRow } from './types.js';

const OPERATIONS = [
  'sea',
  'manoeuvring',
  'port',
  'anchor',
  'drift',
  'canal_strait',
  'waiting',
] as const;
const ALL = '__all__';

export interface VoyageAnalysisPanelProps {
  source: FuelSource;
  vesselId: string | null;
  onVessel?: (data: {
    vessel: { id: string; name: string };
    vessels?: { id: string; name: string }[];
  }) => void;
  /** Ofiste gemi seçimi (filtre kartındaki "Gemi"). */
  onPickVessel?: (vesselId: string) => void;
}

const CHART_W = 560;
const CHART_H = 260;
const PAD = { left: 44, right: 12, top: 26, bottom: 34 };

function axisTop(max: number): number {
  if (max <= 0) return 10;
  const step = 10 ** Math.floor(Math.log10(max));
  for (const factor of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (factor * step >= max) return factor * step;
  }
  return 10 * step;
}

const day = (iso: string) => iso.slice(0, 10);
const dmy = (value: string) => `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`;

/**
 * Voyage Analizi ▸ Operasyon ve Maliyet (ortağın 07-voyage-analizi.jpeg; ortak belgesi §11; ADR
 * 0117 §14, Ek Y4): süzgeçler (gemi, sefer, bacak, CP, tarih, operasyon, yakıt, veri durumu),
 * başlangıç ROB / alım / tüketim / bitiş ROB kartları, operasyonel dönem ve CP kapsamı tablosu,
 * yığılı çubuk grafik, performans göstergeleri (günlük, mil başına, maliyet, eksik veri) ve
 * Excel/PDF. Sonuç başlığında seçilen kapsam yazılır. MRV ve ETS görünümü sonraki aşamada.
 */
export function VoyageAnalysisPanel({
  source,
  vesselId,
  onVessel,
  onPickVessel,
}: VoyageAnalysisPanelProps) {
  const { t, i18n } = useTranslation();
  const shell = useShell();
  const errorText = useLedgerError();
  const [filters, setFilters] = useState<FuelAnalysisFilters>({});
  const [draft, setDraft] = useState<FuelAnalysisFilters>({});
  const [data, setData] = useState<FuelAnalysisData | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [unit, setUnit] = useState<'mt' | 'pct'>('mt');
  // MRV ve ETS görünümü boş durumla (sahip MRV'yi erteledi; ADR 0117 Ek Y4 §17)
  const [view, setView] = useState<'operations' | 'mrv'>('operations');
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!source.analysis) return;
    try {
      const next = await source.analysis(vesselId, filters);
      setData(next);
      setFailed(null);
      onVessel?.(next);
      setDraft({
        ...filters,
        voyageId: next.filters.voyageId,
        leg: next.filters.leg,
        charterPartyId: next.filters.charterPartyId,
        operation: next.filters.operation,
        fuelTypeId: next.filters.fuelTypeId,
        status: next.filters.status,
        from: day(next.filters.from),
        to: day(new Date(Date.parse(next.filters.to) - 1).toISOString()),
      });
    } catch (failure) {
      setFailed(errorCode(failure));
    }
  }, [source, vesselId, filters, onVessel]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!source.analysis) return null;
  if (failed) {
    return <LoadError code={failed} onRetry={() => void load()} title={t('fuel.analysis.title')} />;
  }
  if (!data) return <TableSkeleton columns={6} rows={8} label={t('fuel.analysis.title')} />;

  const fuels = analysisFuels(data);
  const fuelCode = (id: string | null) =>
    data.fuelTypes.find((f) => f.id === id)?.code ?? (id ? id.slice(0, 8) : '—');
  const periodKey = (row: FuelAnalysisRow) =>
    row.cp === 'out' || row.cp === 'partial' ? row.cp : (row.operation ?? 'sea');
  const periodLabel = (row: FuelAnalysisRow) => t(`fuel.analysis.periods.${periodKey(row)}`);
  const operationLabel = (row: FuelAnalysisRow) =>
    row.operation
      ? t(`fuel.analysis.operations.${row.operation}`)
      : t('fuel.analysis.operations.other');
  const coverageLabel = (row: FuelAnalysisRow) => t(`fuel.analysis.coverage.${row.cp}`);
  const voyage = data.options.voyages.find((v) => v.id === data.filters.voyageId) ?? null;
  const range = `${dmy(day(data.filters.from))} – ${dmy(day(new Date(Date.parse(data.filters.to) - 1).toISOString()))}`;
  const scope = voyage
    ? t('fuel.analysis.scopeVoyage', { vessel: data.vessel.name, voyage: voyage.voyageNo, range })
    : t('fuel.analysis.scope', { vessel: data.vessel.name, range });
  const set = <K extends keyof FuelAnalysisFilters>(key: K, value: FuelAnalysisFilters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));
  const pick = (value: string | null) => (value === null || value === ALL ? null : value);
  const apply = (patch: Partial<FuelAnalysisFilters> = {}) => setFilters({ ...draft, ...patch });

  const save = async (fileName: string, bytes: Uint8Array, type: string) => {
    if (source.saveFile) await source.saveFile(fileName, bytes, type);
    else downloadFile(fileName, bytes, type);
  };
  const fileBase = `voyage-analysis-${data.vessel.name.replace(/[^A-Za-z0-9]+/g, '-')}-${day(data.filters.from)}_${day(data.filters.to)}`;
  const generated = new Date().toISOString().slice(0, 16).replace('T', ' ');
  const tableLabels = {
    period: t('fuel.analysis.columns.period'),
    operation: t('fuel.analysis.columns.operation'),
    cp: t('fuel.analysis.columns.cp'),
    total: t('fuel.analysis.columns.total'),
    totalRow: t('fuel.analysis.totalRow'),
  };
  const exportExcel = async () => {
    setExportError(null);
    try {
      const table = analysisWorkbookTable(data, {
        period: periodLabel,
        operation: operationLabel,
        coverage: coverageLabel,
        labels: tableLabels,
      });
      const bytes = await reportWorkbook(table, {
        title: t('fuel.analysis.export.title'),
        period: scope,
        generated: `${t('fuel.analysis.export.generated')}: ${generated}`,
      });
      await save(`${fileBase}.xlsx`, bytes, XLSX_TYPE);
    } catch (failure) {
      setExportError(errorText(failure));
    }
  };
  const exportPdf = async () => {
    setExportError(null);
    try {
      const stockRows = [
        [t('fuel.analysis.cards.startRob'), mt(data.stock.startRob)],
        [t('fuel.analysis.cards.bunkered'), mt(data.stock.bunkered)],
        [t('fuel.analysis.cards.consumed'), mt(data.stock.consumed)],
        [t('fuel.analysis.cards.endRob'), mt(data.stock.endRob)],
      ];
      const bytes = await renderPdf(
        tablePdf({
          title: t('fuel.analysis.export.title'),
          info: [
            [t('fuel.analysis.vessel'), data.vessel.name],
            [t('fuel.analysis.dateRange'), range],
            [t('fuel.analysis.voyage'), voyage?.voyageNo ?? t('fuel.analysis.all')],
            [t('fuel.analysis.status'), t(`fuel.analysis.statuses.${data.filters.status}`)],
            [t('fuel.analysis.export.generated'), generated],
          ],
          tables: [
            { head: ['', 'MT'], rows: stockRows, numeric: [1] },
            {
              title: t('fuel.analysis.table'),
              head: [
                tableLabels.period,
                tableLabels.operation,
                tableLabels.cp,
                ...fuels.map((f) => `${f.code} (MT)`),
                tableLabels.total,
              ],
              numeric: fuels.map((_, i) => i + 3).concat([fuels.length + 3]),
              rows: [
                ...data.table.rows.map((row) => [
                  periodLabel(row),
                  operationLabel(row),
                  coverageLabel(row),
                  ...fuels.map((f) => mt(row.byFuel[f.id] ?? '0')),
                  mt(row.total),
                ]),
                [
                  tableLabels.totalRow,
                  '',
                  '',
                  ...fuels.map((f) => mt(data.table.totals.byFuel[f.id] ?? '0')),
                  mt(data.table.totals.total),
                ],
              ],
            },
            {
              title: t('fuel.analysis.performance'),
              head: ['', ''],
              numeric: [1],
              rows: [
                [
                  t('fuel.analysis.daily'),
                  `${mt(data.performance.dailyMt)} ${t('fuel.analysis.perDay')}`,
                ],
                [
                  t('fuel.analysis.perNm'),
                  `${mt(data.performance.perNmMt, 3)} ${t('fuel.analysis.perNmUnit')}`,
                ],
                [
                  t('fuel.analysis.cost'),
                  data.cost.amount ? `${mt(data.cost.amount)} ${data.cost.currency}` : '—',
                ],
                [t('fuel.analysis.missing'), String(data.missing.length)],
              ],
            },
          ],
          footer: scope,
          notes: data.provisional ? [t('fuel.analysis.provisionalNote')] : [],
        }),
      );
      await save(`${fileBase}.pdf`, bytes, 'application/pdf');
    } catch (failure) {
      setExportError(errorText(failure));
    }
  };

  // Grafik: satır başına yığılı çubuk (yakıt), MT ya da toplam içindeki pay (%)
  const totalAll = Number(data.table.totals.total);
  const scaled = (v: string) =>
    unit === 'pct' ? (totalAll > 0 ? (Number(v) / totalAll) * 100 : 0) : Number(v);
  const top = axisTop(Math.max(0, ...data.table.rows.map((r) => scaled(r.total))));
  const plotW = CHART_W - PAD.left - PAD.right;
  const plotH = CHART_H - PAD.top - PAD.bottom;
  const slot = data.table.rows.length > 0 ? plotW / data.table.rows.length : plotW;
  const barW = Math.min(64, slot * 0.6);
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const ticks = [0, top / 4, top / 2, (3 * top) / 4, top];
  const fmt = (v: number) =>
    unit === 'pct' ? `${Math.round(v * 10) / 10}%` : mt(String(Math.round(v * 100) / 100));
  const locale = i18n.resolvedLanguage ?? 'en';
  const money = (value: string) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(value));
  const go = (path: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (shell) followLink(event, path, shell.navigate);
  };

  const stat = (
    key: 'startRob' | 'bunkered' | 'consumed' | 'endRob',
    tone: 'start' | 'bunker' | 'consumption' | 'end',
    icon: React.ReactNode,
  ) => (
    <div className={cx.stat}>
      <span className={`${cx.statIcon} ${cx[`statIcon_${tone}`]}`} aria-hidden>
        {icon}
      </span>
      <span>
        <span className={cx.statLabel}>
          {t(`fuel.analysis.cards.${key}`)}
          {key === 'consumed' && data.provisional ? (
            <span className={cx.provisional}>{t('fuel.analysis.provisional')}</span>
          ) : null}
        </span>
        <span className={cx.statValue}>
          {mt(data.stock[key])}
          <span className={cx.statUnit}>MT</span>
        </span>
      </span>
    </div>
  );

  return (
    <div className={classes.alerts}>
      <section className={classes.card} aria-label={t('fuel.analysis.title')}>
        <form
          className={cx.filters}
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          {data.scope === 'web' && (data.vessels ?? []).length > 0 ? (
            <Select
              label={t('fuel.analysis.vessel')}
              data={(data.vessels ?? []).map((v) => ({ value: v.id, label: v.name }))}
              value={data.vessel.id}
              allowDeselect={false}
              onChange={(v) => {
                if (v && onPickVessel) onPickVessel(v);
              }}
            />
          ) : (
            <TextInput label={t('fuel.analysis.vessel')} value={data.vessel.name} readOnly />
          )}
          <Select
            label={t('fuel.analysis.voyage')}
            data={[
              { value: ALL, label: t('fuel.analysis.all') },
              ...data.options.voyages.map((v) => ({ value: v.id, label: v.voyageNo })),
            ]}
            value={draft.voyageId ?? ALL}
            allowDeselect={false}
            onChange={(v) => {
              const id = pick(v);
              const chosen = data.options.voyages.find((x) => x.id === id);
              setDraft((prev) => ({
                ...prev,
                voyageId: id,
                leg: null,
                ...(chosen?.start
                  ? {
                      from: day(chosen.start),
                      to: chosen.end
                        ? day(new Date(Date.parse(chosen.end) - 1).toISOString())
                        : day(new Date().toISOString()),
                    }
                  : {}),
              }));
            }}
          />
          <Select
            label={t('fuel.analysis.leg')}
            data={[
              { value: ALL, label: t('fuel.analysis.all') },
              ...data.options.legs.map((n) => ({
                value: String(n),
                label: t('fuel.analysis.legOption', { no: String(n).padStart(2, '0') }),
              })),
            ]}
            value={draft.leg ? String(draft.leg) : ALL}
            allowDeselect={false}
            disabled={!draft.voyageId}
            onChange={(v) => set('leg', pick(v) ? Number(v) : null)}
          />
          <Select
            label={t('fuel.analysis.cp')}
            data={[
              { value: ALL, label: t('fuel.analysis.all') },
              ...data.options.charterParties.map((cp) => ({
                value: cp.id,
                label: cp.cpNo ?? cp.id.slice(0, 8),
              })),
            ]}
            value={draft.charterPartyId ?? ALL}
            allowDeselect={false}
            onChange={(v) => set('charterPartyId', pick(v))}
          />
          <div className={cx.dateRange}>
            <TextInput
              type="date"
              label={t('fuel.analysis.from')}
              value={draft.from ?? ''}
              onChange={(e) => set('from', e.currentTarget.value || null)}
            />
            <TextInput
              type="date"
              label={t('fuel.analysis.to')}
              value={draft.to ?? ''}
              onChange={(e) => set('to', e.currentTarget.value || null)}
            />
          </div>
          <Select
            label={t('fuel.analysis.operation')}
            data={[
              { value: ALL, label: t('fuel.analysis.all') },
              ...OPERATIONS.map((o) => ({ value: o, label: t(`fuel.analysis.operations.${o}`) })),
            ]}
            value={draft.operation ?? ALL}
            allowDeselect={false}
            onChange={(v) => set('operation', pick(v))}
          />
          <Select
            label={t('fuel.analysis.fuel')}
            data={[
              { value: ALL, label: t('fuel.analysis.all') },
              ...data.fuelTypes.map((f) => ({ value: f.id, label: f.code })),
            ]}
            value={draft.fuelTypeId ?? ALL}
            allowDeselect={false}
            onChange={(v) => set('fuelTypeId', pick(v))}
          />
          <Select
            label={t('fuel.analysis.status')}
            data={(['approved', 'all'] as const).map((s) => ({
              value: s,
              label: t(`fuel.analysis.statuses.${s}`),
            }))}
            value={draft.status ?? data.filters.status}
            allowDeselect={false}
            onChange={(v) => set('status', (v as 'approved' | 'all') ?? 'approved')}
          />
          <div className={cx.filterActions}>
            <Button type="submit" leftSection={<IconSearch size={18} />}>
              {t('fuel.analysis.show')}
            </Button>
            <Button
              variant="outline"
              leftSection={<IconFileSpreadsheet size={18} />}
              onClick={() => void exportExcel()}
            >
              {t('fuel.analysis.excel')}
            </Button>
            <Button
              variant="outline"
              leftSection={<IconFileTypePdf size={18} />}
              onClick={() => void exportPdf()}
            >
              {t('fuel.analysis.pdf')}
            </Button>
          </div>
        </form>
        <div className={cx.views}>
          <SegmentedControl
            aria-label={t('fuel.analysis.title')}
            value={view}
            onChange={(value) => setView(value === 'mrv' ? 'mrv' : 'operations')}
            data={[
              { value: 'operations', label: t('fuel.analysis.views.operations') },
              { value: 'mrv', label: t('fuel.analysis.views.mrv') },
            ]}
          />
          <span className={cx.scope}>{scope}</span>
        </div>
      </section>
      {view === 'mrv' ? (
        <section className={classes.card} aria-label={t('fuel.analysis.views.mrv')}>
          <div className={classes.empty}>
            <IconChartBar size={36} className={classes.emptyIcon} aria-hidden />
            {t('fuel.analysis.mrvSoon')}
          </div>
        </section>
      ) : null}
      {view === 'operations' && exportError ? (
        <Alert color="red" role="alert" className={classes.banner}>
          {exportError}
        </Alert>
      ) : null}
      {view === 'operations' && data.provisional ? (
        <Alert color="yellow" role="status" className={classes.banner}>
          {t('fuel.analysis.provisionalNote')}
        </Alert>
      ) : null}
      {view === 'operations' ? (
        <>
          <div className={cx.stats}>
            {stat('startRob', 'start', <IconGasStation size={30} />)}
            {stat('bunkered', 'bunker', <IconArrowDown size={30} />)}
            {stat('consumed', 'consumption', <IconArrowUp size={30} />)}
            {stat('endRob', 'end', <IconGasStation size={30} />)}
          </div>

          <div className={cx.analysisRow}>
            <section
              className={`${classes.card} ${classes.panel}`}
              aria-label={t('fuel.analysis.table')}
            >
              <h2 className={classes.panelTitle}>{t('fuel.analysis.table')}</h2>
              <div
                className={classes.tableWrap}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${classes.table} ${cx.analysisTable}`}>
                  <thead>
                    <tr>
                      <th>{tableLabels.period}</th>
                      <th>{tableLabels.operation}</th>
                      <th>{tableLabels.cp}</th>
                      {fuels.map((f) => (
                        <th key={f.id} className={classes.num}>
                          {f.code} (MT)
                        </th>
                      ))}
                      <th className={classes.num}>{tableLabels.total}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.table.rows.length === 0 ? (
                      <tr>
                        <td colSpan={4 + fuels.length}>{t('fuel.analysis.empty')}</td>
                      </tr>
                    ) : (
                      data.table.rows.map((row) => (
                        <tr key={row.key}>
                          <td>
                            <span className={cx.periodCell}>
                              <span
                                className={`${cx.periodBar} ${cx[`periodBar_${periodKey(row)}`] ?? ''}`}
                                aria-hidden
                              />
                              {periodLabel(row)}
                            </span>
                          </td>
                          <td>{operationLabel(row)}</td>
                          <td>
                            <span className={`${cx.coverage} ${cx[`coverage_${row.cp}`] ?? ''}`}>
                              {coverageLabel(row)}
                            </span>
                          </td>
                          {fuels.map((f) => (
                            <td key={f.id} className={classes.num}>
                              {mt(row.byFuel[f.id] ?? '0')}
                            </td>
                          ))}
                          <td className={classes.num}>
                            <strong>{mt(row.total)}</strong>
                          </td>
                        </tr>
                      ))
                    )}
                    <tr className={cx.totalRowCells}>
                      <td>{tableLabels.totalRow}</td>
                      <td>-</td>
                      <td>-</td>
                      {fuels.map((f) => (
                        <td key={f.id} className={classes.num}>
                          {mt(data.table.totals.byFuel[f.id] ?? '0')}
                        </td>
                      ))}
                      <td className={classes.num}>{mt(data.table.totals.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section
              className={`${classes.card} ${classes.panel}`}
              aria-label={t('fuel.analysis.chart')}
            >
              <div className={classes.panelHead}>
                <h2 className={classes.panelTitle}>{t('fuel.analysis.chart')}</h2>
                <Select
                  className={cx.unitSelect}
                  aria-label={t('fuel.analysis.unit')}
                  data={(['mt', 'pct'] as const).map((u) => ({
                    value: u,
                    label: t(`fuel.analysis.units.${u}`),
                  }))}
                  value={unit}
                  allowDeselect={false}
                  onChange={(v) => setUnit((v as 'mt' | 'pct') ?? 'mt')}
                />
              </div>
              {data.table.rows.length === 0 ? (
                <div className={classes.empty}>
                  <IconChartBar size={36} className={classes.emptyIcon} aria-hidden />
                  {t('fuel.analysis.empty')}
                </div>
              ) : (
                <figure className={classes.chart}>
                  <div className={classes.chartHead}>
                    <span className={classes.hint}>
                      {t('fuel.analysis.chartAxis', { unit: t(`fuel.analysis.units.${unit}`) })}
                    </span>
                    <span className={classes.legend}>
                      {fuels.map((f, index) => (
                        <span className={classes.legendItem} key={f.id}>
                          <span
                            className={`${classes.dot} ${cx[`legendDot${Math.min(index, 2)}`]}`}
                            aria-hidden
                          />
                          {f.code}
                        </span>
                      ))}
                    </span>
                  </div>
                  <svg
                    viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                    className={cx.bars}
                    role="img"
                    aria-label={t('fuel.analysis.chart')}
                  >
                    {ticks.map((tick) => (
                      <g key={tick}>
                        <line
                          x1={PAD.left}
                          x2={CHART_W - PAD.right}
                          y1={y(tick)}
                          y2={y(tick)}
                          className={cx.grid}
                        />
                        <text x={PAD.left - 6} y={y(tick) + 4} className={cx.axis} textAnchor="end">
                          {Math.round(tick * 10) / 10}
                        </text>
                      </g>
                    ))}
                    {data.table.rows.map((row, index) => {
                      const x = PAD.left + slot * index + (slot - barW) / 2;
                      let base = 0;
                      return (
                        <g key={row.key}>
                          {fuels.map((f, fi) => {
                            const v = scaled(row.byFuel[f.id] ?? '0');
                            const rect = (
                              <rect
                                key={f.id}
                                x={x}
                                width={barW}
                                y={y(base + v)}
                                height={Math.max(0, y(base) - y(base + v))}
                                className={cx[`seg${Math.min(fi, 2)}`]}
                              >
                                <title>{`${periodLabel(row)} · ${f.code}: ${fmt(v)}`}</title>
                              </rect>
                            );
                            base += v;
                            return rect;
                          })}
                          <text
                            x={x + barW / 2}
                            y={y(base) - 6}
                            className={cx.barLabel}
                            textAnchor="middle"
                          >
                            {fmt(base)}
                          </text>
                          <text
                            x={x + barW / 2}
                            y={CHART_H - 12}
                            className={cx.axis}
                            textAnchor="middle"
                          >
                            {periodLabel(row)}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </figure>
              )}
            </section>
          </div>

          <section className={classes.card} aria-label={t('fuel.analysis.performance')}>
            <div className={classes.head}>
              <h2 className={classes.title}>{t('fuel.analysis.performance')}</h2>
            </div>
            <div className={cx.perf}>
              <div className={cx.perfBox}>
                <div className={cx.perfItem}>
                  <span className={cx.perfIcon} aria-hidden>
                    <IconGauge size={24} />
                  </span>
                  <span>
                    <span className={cx.statLabel}>{t('fuel.analysis.daily')}</span>
                    <span
                      className={cx.perfValue}
                      title={t('fuel.analysis.hoursBasis', {
                        hours: Math.round(data.performance.hours),
                      })}
                    >
                      {mt(data.performance.dailyMt)}{' '}
                      <span className={cx.statUnit}>{t('fuel.analysis.perDay')}</span>
                    </span>
                  </span>
                </div>
                <div className={cx.perfItem}>
                  <span className={cx.perfIcon} aria-hidden>
                    <IconMapPin size={24} />
                  </span>
                  <span>
                    <span className={cx.statLabel}>{t('fuel.analysis.perNm')}</span>
                    <span
                      className={cx.perfValue}
                      title={t('fuel.analysis.distanceBasisHint', {
                        nm: data.performance.distanceNm,
                      })}
                    >
                      {mt(data.performance.perNmMt, 2)}{' '}
                      <span className={cx.statUnit}>{t('fuel.analysis.perNmUnit')}</span>
                    </span>
                  </span>
                </div>
                <Select
                  className={cx.perfSelect}
                  label={t('fuel.analysis.distanceBasis')}
                  data={(['sog', 'stw'] as const).map((d) => ({
                    value: d,
                    label: t(`fuel.analysis.distanceBases.${d}`),
                  }))}
                  value={data.filters.distanceBasis}
                  allowDeselect={false}
                  onChange={(v) => apply({ distanceBasis: (v as 'sog' | 'stw') ?? 'sog' })}
                />
              </div>
              <div className={cx.perfBox}>
                <div className={cx.perfItem}>
                  <span className={cx.perfIcon} aria-hidden>
                    <IconCoins size={24} />
                  </span>
                  <span>
                    <span className={cx.statLabel}>{t('fuel.analysis.cost')}</span>
                    <span className={cx.perfValue}>
                      {data.cost.amount ? money(data.cost.amount) : '—'}{' '}
                      <span className={cx.statUnit}>{data.cost.currency}</span>
                    </span>
                    {data.cost.missingFuels.length > 0 ? (
                      <span className={classes.hint}>
                        {t('fuel.analysis.costMissing', {
                          fuels: data.cost.missingFuels.map(fuelCode).join(', '),
                        })}
                      </span>
                    ) : null}
                  </span>
                </div>
                <Select
                  className={cx.perfSelect}
                  label={t('fuel.analysis.costMethod')}
                  data={(['weighted_average', 'batch'] as const).map((m) => ({
                    value: m,
                    label: t(`fuel.analysis.costMethods.${m}`),
                  }))}
                  value={data.filters.costMethod}
                  allowDeselect={false}
                  onChange={(v) =>
                    apply({ costMethod: (v as 'weighted_average' | 'batch') ?? 'weighted_average' })
                  }
                />
              </div>
              <div className={cx.missing}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                  <IconAlertTriangle size={22} className={cx.missingIcon} aria-hidden />
                  {t('fuel.analysis.missing')}
                </span>
                <span className={cx.missingValue}>
                  {t('fuel.analysis.records', { count: data.missing.length })}
                </span>
                <button
                  type="button"
                  className={cx.missingLink}
                  onClick={() => setSourcesOpen(true)}
                >
                  {t('fuel.analysis.sources')} →
                </button>
              </div>
            </div>
          </section>
        </>
      ) : null}

      <Modal
        opened={sourcesOpen}
        onClose={() => setSourcesOpen(false)}
        title={t('fuel.analysis.sourcesTitle')}
        size="xl"
        centered
      >
        {data.missing.length > 0 ? (
          <ul className={cx.missingList}>
            {data.missing.map((m) => (
              <li key={`${m.code}-${m.reportId ?? m.fuelTypeId}`}>
                {t(`fuel.analysis.missingCodes.${m.code}`, { fuel: fuelCode(m.fuelTypeId) })}
                {m.reportId ? (
                  <>
                    {' · '}
                    <a
                      className={classes.link}
                      href={`/fuel/consumption?report=${m.reportId}`}
                      onClick={go(`/fuel/consumption?report=${m.reportId}`)}
                    >
                      {m.reportNo}
                    </a>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        <h3 className={classes.panelTitle}>{t('fuel.analysis.intervalsTitle')}</h3>
        <div
          className={classes.tableWrap}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${classes.table} ${classes.compact}`}>
            <thead>
              <tr>
                <th>{t('fuel.analysis.intervalColumns.report')}</th>
                <th>{t('fuel.analysis.intervalColumns.period')}</th>
                <th>{t('fuel.analysis.intervalColumns.operation')}</th>
                <th>{t('fuel.analysis.intervalColumns.leg')}</th>
                <th>{t('fuel.analysis.intervalColumns.cp')}</th>
                <th className={classes.num}>{t('fuel.analysis.intervalColumns.total')}</th>
              </tr>
            </thead>
            <tbody>
              {data.intervals.map((item) => (
                <tr key={item.reportId}>
                  <td>
                    <a
                      className={classes.link}
                      href={`/fuel/consumption?report=${item.reportId}`}
                      onClick={go(`/fuel/consumption?report=${item.reportId}`)}
                    >
                      {item.reportNo}
                    </a>
                    {item.provisional ? (
                      <span className={cx.provisional}>{t('fuel.analysis.provisional')}</span>
                    ) : null}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {`${item.periodStart.slice(0, 16).replace('T', ' ')} – ${item.periodEnd.slice(0, 16).replace('T', ' ')}`}
                  </td>
                  <td>{t(`fuel.analysis.operations.${item.operation}`)}</td>
                  <td>{item.leg ?? '—'}</td>
                  <td>{t(`fuel.analysis.coverage.${item.cp}`)}</td>
                  <td className={classes.num}>
                    {mt(formatDec(decSum(Object.values(item.byFuel))))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <Button variant="default" onClick={() => setSourcesOpen(false)}>
            {t('fuel.analysis.close')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
